import {
  formatEther,
  getAddress,
  isAddress,
  parseEther,
  parseUnits,
} from "viem";
import * as YupNative from "yup";

export type Schema = YupNative.Schema;
export type ObjectSchema = YupNative.ObjectSchema<any>;
export type SchemaObjectDescription = YupNative.SchemaObjectDescription;

/**
 * This adds methods to yup's standard types.
 * It also transform certain values to make them easily usable by our API.
 * The most obvious example is the "eth" method, which transforms a string to a wei value(0.2 => 200000000000000000)
 *
 * @example
 * const values_ = schema.cast(values)
 * await fetchAPI(url, {
 *  method: "POST",
 *  body: values_,
 * })
 */

// Usage: yup.string().eth().minEth(0.2, "your message here")
// transform values to wei when casting, before submitting the form
// to be used in conjunction of schema.cast()
// When validating a value that is already in wei,
// This _must_ be a string, because the API expects a string(WEI) to prevent overflows,
// and yup throws if it receives a string while the initial type was a number.
YupNative.addMethod(YupNative.string, "eth", function ethify() {
  return this.meta({
    unit: "eth",
    // we store everything in WEI, but show to the user in ETH.
    parseToInput: (value) => {
      return value ? formatEther(value) : "";
    },
  }).when("$unit", {
    is: "wei",
    // TODO: it seems that the value is now always transformed before we check,
    // so i'm not sure the context still applies 🤔
    then: (sch) =>
      sch.test("valid-wei", "Invalid wei value", function testWei(value) {
        if (!value) {
          return true;
        }
        try {
          parseUnits(value, 18).toString();
          return true;
        } catch {
          return false;
        }
      }),
    otherwise: (sch) =>
      sch.transform((value) => {
        console.log(
          "transforming",
          value,
          parseEther(value.toString()).toString(),
        );
        if (!value || value === "") {
          return null;
        }
        return parseEther(value.toString()).toString();
      }),
  });
});

YupNative.addMethod(
  YupNative.string,
  "minEth",
  function minEth(
    min: number | bigint,
    message = `Minimum amount is ${
      typeof min === "bigint" ? formatEther(min) : min
    } ETH`,
  ) {
    const parsed = typeof min === "bigint" ? parseFloat(formatEther(min)) : min;
    return this.meta({
      props: {
        min: parsed,
        ...this.describe().meta?.props,
      },
    }).test("min-eth", message, (value) => {
      if (!value || value === "") {
        return true;
      }

      const minInWei = parseEther(parsed.toString());
      const parsedValue = BigInt(value);
      return minInWei <= parsedValue;
    });
  },
);

YupNative.addMethod(
  YupNative.string,
  "maxEth",
  function maxEth(
    max: number | bigint,
    message = `Maximum amount is ${
      typeof max === "bigint" ? formatEther(max) : max
    } ETH`,
  ) {
    if (max === undefined) {
      return this;
    }
    const parsedMax =
      typeof max === "bigint" ? parseFloat(formatEther(max)) : max;
    return this.meta({
      props: {
        max,
        ...this.describe().meta?.props,
      },
    }).test("max-eth", message, (value) => {
      if (!value || value === "") {
        return true;
      }

      const maxInWei = parseEther(parsedMax.toString());
      const parsedValue = BigInt(value);
      return maxInWei >= parsedValue;
    });
  },
);

// Usage: yup.string().isAddress("My Custom Message").required()
YupNative.addMethod(
  YupNative.string,
  "isAddress",
  function yupIsAddress(message = "is not valid") {
    return this.meta({
      props: {
        placeholder: "0x",
        ...this.describe().meta?.props,
      },
    })
      .test("is-address", message, (value) => {
        if (value && value !== "") {
          return isAddress(value);
        }
        return true;
      })
      .transform((value) => {
        if (value && isAddress(value)) {
          return getAddress(value);
        }
        return value;
      });
  },
);

// Usage: yup.number().nullify().
// useful when casting before submitting: const values = schema.cast(valuesIn)
// vs code shows an error, but there are none 😭
// @ts-ignore This seems perfectly fine, the nullable method is there, so hiding this ts error
YupNative.addMethod(YupNative.number, "nullify", function nullifyIfEmpty() {
  return this.transform((value) => {
    return value === "" || isNaN(value) ? null : value;
  }).nullable();
});

// Usage: yup.number().nullify().
// useful when casting before submitting: const values = schema.cast(valuesIn)
// vs code shows an error, but there are none 😭
// @ts-ignore This seems perfectly fine, the nullable method is there, so hiding this ts error
YupNative.addMethod(YupNative.string, "nullify", function nullifyIfEmpty() {
  return this.transform((value) => {
    return value === "" ? null : value;
  }).nullable();
});

// Usage: yup.date().nullify().
// useful when casting before submitting: const values = schema.cast(valuesIn)
// vs code shows an error, but there are none 😭
// @ts-ignore This seems perfectly fine, the nullable method is there, so hiding this ts error
YupNative.addMethod(YupNative.date, "nullify", function nullifyIfEmpty() {
  return this.nullable().transform((v: any) =>
    v === "" || v === null ? null : v,
  );
});

// Usage: yup.number().required()
YupNative.addMethod(
  YupNative.number,
  "isTwoDecimalMax",
  function isTwoDecimalMax(message = "Maximum two decimal places") {
    return this.meta({
      props: {
        ...this.describe().meta?.props,
      },
    }).test("is-two-decimals", message, (value) => {
      if (value) {
        const split = value.toString().split(".");
        return split.length === 1 || (split?.[1]?.length ?? 0) <= 2;
      }
      return true;
    });
  },
);

export default YupNative as typeof YupNative;

// for some reason we can't add requiredIfFinal to MixedSchema only
// so we add to every primitive type's schemas... it's ok.
declare module "yup" {
  interface Schema {
    requiredIfFinal(
      msg?: string,
      schematize?: (schemaIn: YupNative.Schema) => this,
    ): this;
  }
  interface StringSchema {
    eth(): this;
    minEth(min: number | bigint, message?: string | (() => string)): this;
    maxEth(min: number | bigint, message?: string | (() => string)): this;
    isAddress(message?: string | (() => string)): this;
    nullify(): this;
  }
  interface NumberSchema {
    nullify(): this;
    isTwoDecimalMax(message?: string | (() => string)): this;
  }
  interface DateSchema {
    nullify(): this;
  }
}
